<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

import skoreLogo from "@/assets/images/skore-text.svg";
import BreadcrumbNavigation, {
  type BreadcrumbItem,
} from "@/components/ui/BreadcrumbNavigation.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName } from "@/dto";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";

const { breadcrumbItems } = defineProps<{ breadcrumbItems: BreadcrumbItem[] }>();

const api = useApi();
const router = useRouter();
const myName = ref("");
const dropdown = useTemplateRef<InstanceType<typeof DropdownButton>>("dropdown");
const links = [
  {
    name: "Skore Hub Documentation",
    icon: "icon-book",
    href: "https://probabl.notion.site/skore-hub",
  },
  {
    name: "Skore on GitHub",
    icon: "icon-github",
    href: "https://github.com/probabl-ai/skore",
  },
  {
    name: "Community Support",
    icon: "icon-discord",
    href: "https://discord.probabl.ai",
  },
  {
    name: "Request Professional Support",
    icon: "icon-buoy",
    href: "https://hello.probabl.ai/contact-us-0?utm_source=skore_hub&utm_medium=inapp&utm_campaign=pro_support",
  },
  {
    name: "Your Account",
    icon: "icon-user",
    href: import.meta.env.VITE_VUE_IDP_PROFILE_SETTINGS_URL,
  },
];

function onLinkClick(href: string) {
  console.log(href);
  window.open(href, "_blank")?.focus();
}

function onLogout() {
  router.push({ name: RouteNames.Logout });
}

async function fetchMe() {
  const me = await api.fetchMe();
  if (me) {
    myName.value = getUserFriendlyName(me);
  }
}

onMounted(async () => {
  await fetchMe();
});
</script>

<template>
  <div class="app-toolbar">
    <div class="logo" @click="router.push({ name: RouteNames.Dispatch })">
      <img :src="skoreLogo" alt="skore" />
    </div>
    <nav>
      <BreadcrumbNavigation :items="breadcrumbItems" />
    </nav>
    <div class="profile">
      <UserAvatar :name="myName" @click.stop="dropdown?.toggle()" />
      <DropdownButton
        icon-left="icon-chevron-up-down"
        ref="dropdown"
        kind="ghost"
        :allowed-placements="['bottom-end']"
      >
        <DropdownButtonItem
          v-for="link in links"
          :key="link.name"
          :icon-left="link.icon"
          :label="link.name"
          @click="onLinkClick(link.href)"
        />
        <DropdownButtonItem icon-left="icon-logout" label="Logout" @click="onLogout" />
      </DropdownButton>
    </div>
  </div>
</template>

<style scoped>
.app-toolbar {
  display: flex;
  width: 100%;
  height: var(--height-header);
  flex-direction: row;
  padding: 0 var(--page-padding);
  border-bottom: solid var(--stroke-width-m) var(--color-stroke-background-primary);
  background-color: var(--color-background-primary);

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    & > img {
      transform: translateY(1px);
    }
  }

  nav {
    display: flex;
    flex: 1;
    flex-direction: row;
    align-items: center;
    margin-left: var(--spacing-8);
  }

  .profile {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 0 0 0 var(--spacing-8);
    border-radius: var(--radius-m);
    margin: var(--spacing-4) 0;
    cursor: pointer;
    transition: background-color var(--animation-duration) var(--animation-easing);

    &:hover {
      background-color: var(--color-button-background-tertiary-hover);
    }

    :deep(.button) {
      padding: 0 var(--spacing-8) 0 0;
      border: none;
    }

    :deep(.items) {
      margin-top: var(--spacing-6);
    }
  }
}
</style>
